import React, { useEffect, useMemo, useState } from 'react';
import { Form, Radio, Spin } from 'antd';
import { useToken } from '@/common/helper';
import TeamsSelector from '@/components/TeamsSelector';
import { getTeamsDDLOFFeature } from '@/common/utils/dropDown';
import '@/assets/styles/CustomRadioGroup.css';
import theme from '@/assets/theme';
import { themeT } from '@/utils/themeText';

type FilterSetVisibility = 'personal' | 'team' | 'public';

interface TeamOption {
  id: string;
  name: string;
}

interface Props {
  value: FilterSetVisibility;
  onChange: (value: FilterSetVisibility) => void;
  selectedTeamIds: string[];
  onTeamIdsChange: (ids: string[]) => void;
}

const VisibilitySelector: React.FC<Props> = ({
  value,
  onChange,
  selectedTeamIds,
  onTeamIdsChange,
}) => {
  const { token } = useToken();
  const [teams, setTeams] = useState<TeamOption[]>([]);
  const [teamsLoaded, setTeamsLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setTeamsLoaded(false);

    getTeamsDDLOFFeature(token, 'Shipment')
      .then((val) => {
        if (cancelled) return;
        setTeams(val ?? []);
      })
      .catch(() => {
        if (cancelled) return;
        setTeams([]);
      })
      .finally(() => {
        if (!cancelled) {
          setTeamsLoaded(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [token]);

  const hasValidTeams = useMemo(() => {
    return !(
      teams.length === 0 ||
      (teams.length === 1 && teams[0].name?.toLowerCase() === 'default')
    );
  }, [teams]);

  useEffect(() => {
    if (!teamsLoaded) return;

    if (!hasValidTeams && value === 'team') {
      onChange('personal');
      onTeamIdsChange([]);
    }
  }, [teamsLoaded, hasValidTeams, value, onChange, onTeamIdsChange]);

  const selectedTeams = useMemo(
    () => teams.filter((t) => selectedTeamIds.includes(String(t.id))),
    [teams, selectedTeamIds]
  );

  const showTeamOption = !teamsLoaded || hasValidTeams;

  return (
    <Radio.Group
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}
    >
      <Radio value='personal' className='custom-radio has-description'>
        <div className='radio-content'>
          <div className='radio-title'>Personal</div>
          <div className='radio-desc'>Only visible to you</div>
        </div>
      </Radio>

      {showTeamOption && (
        <div>
          <Radio value='team' className='custom-radio has-description'>
            <div className='radio-content'>
              <div className='radio-title'>
                {themeT('team', theme.themeType)}
              </div>
              <div className='radio-desc'>
                Visible to a specific {themeT('team', theme.themeType)} or{' '}
                {themeT('team', theme.themeType)}s
              </div>
            </div>
          </Radio>

          {value === 'team' && (
            <div style={{ marginTop: 8, marginLeft: 28 }}>
              {!teamsLoaded ? (
                <div
                  style={{
                    minHeight: 36,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <Spin size='small' />
                </div>
              ) : hasValidTeams ? (
                <Form.Item
                  name='visibilityTeams'
                  style={{ marginBottom: 0 }}
                  rules={[
                    {
                      validator: async () => {
                        if (value === 'team' && selectedTeamIds.length === 0) {
                          throw new Error(
                            `Please select at least one ${themeT(
                              'team',
                              theme.themeType
                            ).toLowerCase()}`
                          );
                        }
                      },
                    },
                  ]}
                >
                  <TeamsSelector
                    teamsOptions={teams}
                    selectedTeams={selectedTeams}
                    onChange={(next: TeamOption[]) =>
                      onTeamIdsChange(next.map((t) => String(t.id)))
                    }
                    formState='add'
                  />
                </Form.Item>
              ) : null}
            </div>
          )}
        </div>
      )}

      <Radio value='public' className='custom-radio has-description'>
        <div className='radio-content'>
          <div className='radio-title'>Public</div>
          <div className='radio-desc'>Visible to everyone in the account</div>
        </div>
      </Radio>
    </Radio.Group>
  );
};

export default VisibilitySelector;
